import { faArrowRotateRight } from '@fortawesome/pro-regular-svg-icons/faArrowRotateRight';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ActionIcon, Alert, Stack, Text, Title } from '@mantine/core';
import { clsx } from 'clsx';
import { useIntl } from 'react-intl';

import { wizardRatesPanelMessages } from './wizard-rates-panel-messages';
import { WizardCostBreakdown } from './WizardCostBreakdown';
import { WizardFreightRateCard } from './WizardFreightRateCard';
import { WizardFreightRateCardSkeleton } from './WizardFreightRateCardSkeleton';
import { WizardInsuranceFooter } from './WizardInsuranceFooter';
import styles from './WizardRatesPanel.module.css';
import { FREIGHT_QUOTE_ERROR_CODE } from '../../../../ltl/constants';
import { FreightNoCarriersMessage } from '../../../freight-no-carriers-message/freight-no-carriers-message';
import { FilteredRatesEmptyState } from '../../../freight-rate-card/FilteredRatesEmptyState';
import { RateFilterMenu } from '../../../freight-rate-card/RateFilterMenu';
import { RateSearchInput } from '../../../freight-rate-card/RateSearchInput';
import { RateSortMenu } from '../../../freight-rate-card/RateSortMenu';
import { useRateRows } from '../../../freight-rate-card/use-rate-rows';

import type { LtlQuote } from '../../../../types/ltl';
import type { WizardInsurance } from '../../freight-booking-wizard-types';

const SKELETON_ROW_COUNT = 3;

type WizardRatesPanelProps = {
  rates: LtlQuote[];
  isLoadingRates: boolean;
  rateError?: string;
  rateErrorCode?: string | null;
  selectedRate: LtlQuote | null;
  onSelectRate: (quote: LtlQuote) => void;
  onRefresh: () => void;
  insurance: WizardInsurance;
  onInsuranceChange: (next: WizardInsurance) => void;
  sellerFreightProviderId?: number;
  isReadyToQuote?: boolean;
  zIndex?: number;
};

export const WizardRatesPanel = ({
  rates,
  isLoadingRates,
  rateError,
  rateErrorCode,
  selectedRate,
  onSelectRate,
  onRefresh,
  insurance,
  onInsuranceChange,
  sellerFreightProviderId,
  isReadyToQuote = false,
  zIndex,
}: WizardRatesPanelProps) => {
  const intl = useIntl();
  const {
    carrierRows,
    sortOption,
    setSortOption,
    filters,
    setFilters,
    clearRateFilters,
    hasFilteredOutAllRates,
    searchTerm,
    isSearchOpen,
    openSearch,
    closeSearch,
    collapseEmptySearch,
    handleSearchChange,
  } = useRateRows({
    rates,
    isInsured: insurance.isInsured,
    isLoading: isLoadingRates,
  });
  const hasRates = carrierRows.length > 0;
  const hasNoInsuredRates = insurance.isInsured && rates.length > 0 && !hasRates;
  const isQuoteIneligible = rateErrorCode === FREIGHT_QUOTE_ERROR_CODE.QuoteIneligible;

  let emptyState = isReadyToQuote
    ? { message: wizardRatesPanelMessages.noRates, testId: 'wizard-rates-empty' }
    : { message: wizardRatesPanelMessages.completeRequiredFields, testId: 'wizard-rates-incomplete' };
  if (hasNoInsuredRates) {
    emptyState = { message: wizardRatesPanelMessages.noInsuredRates, testId: 'wizard-rates-no-insured-rates' };
  }

  return (
    <div className={styles.panel}>
      <div className={styles.scrollArea}>
        <div className={styles.header}>
          <div className={styles.titleGroup}>
            <Title fw={600} order={3}>
              {intl.formatMessage(wizardRatesPanelMessages.title)}
            </Title>
            <ActionIcon
              aria-label={intl.formatMessage(wizardRatesPanelMessages.refreshAriaLabel)}
              disabled={isLoadingRates || !isReadyToQuote}
              onClick={onRefresh}
              variant='ghost'
            >
              <FontAwesomeIcon
                className={clsx(styles.refreshIcon, isLoadingRates && styles.refreshIconSpinning)}
                icon={faArrowRotateRight}
              />
            </ActionIcon>
          </div>
          <div className={styles.headerActions}>
            <RateSortMenu
              onSortOptionChange={setSortOption}
              onTriggerInteract={collapseEmptySearch}
              sortOption={sortOption}
              zIndex={zIndex}
            />
            <RateFilterMenu filters={filters} onApplyFilters={setFilters} onTriggerInteract={collapseEmptySearch} zIndex={zIndex} />
            <RateSearchInput
              isOpen={isSearchOpen}
              onClose={closeSearch}
              onOpen={openSearch}
              onSearchChange={handleSearchChange}
              searchTerm={searchTerm}
            />
          </div>
        </div>
        {(isLoadingRates || hasRates) && (
          <div className={styles.columnHeader} data-testid='wizard-rates-column-header'>
            <div className={styles.columnHeaderSpacer} />
            <Text className={styles.columnHeaderLabel} component='span' fw={600} size='sm'>
              {intl.formatMessage(wizardRatesPanelMessages.standardColumnHeader)}
            </Text>
            <Text className={styles.columnHeaderLabel} component='span' fw={600} size='sm'>
              {intl.formatMessage(wizardRatesPanelMessages.guaranteedColumnHeader)}
            </Text>
          </div>
        )}
        <Stack className={styles.body} gap='md'>
          {rateError && !isQuoteIneligible && (
            <Alert color='red' data-testid='wizard-rate-error'>
              {rateError}
            </Alert>
          )}
          {!isLoadingRates && hasNoInsuredRates && !rateError && !isQuoteIneligible && (
            <Alert color='red' data-testid='wizard-insured-rates-error'>
              {intl.formatMessage(wizardRatesPanelMessages.insuredRatesError)}
            </Alert>
          )}
          {isLoadingRates && (
            <div className={styles.ratesList}>
              {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
                <WizardFreightRateCardSkeleton key={`wizard-rate-skeleton-${index}`} />
              ))}
            </div>
          )}
          {!isLoadingRates && isQuoteIneligible && !hasRates && (
            <div className={styles.emptyState}>
              <FreightNoCarriersMessage />
            </div>
          )}
          {hasFilteredOutAllRates && <FilteredRatesEmptyState onClearFilters={clearRateFilters} />}
          {!isLoadingRates && !hasFilteredOutAllRates && !hasRates && !rateError && !isQuoteIneligible && (
            <div className={styles.emptyState}>
              <Text className={styles.emptyState} c='dimmed' data-testid={emptyState.testId}>
                {intl.formatMessage(emptyState.message)}
              </Text>
            </div>
          )}
          {!isLoadingRates && hasRates && (
            <div className={styles.ratesList}>
              {carrierRows.map((carrierRow) => (
                <WizardFreightRateCard
                  carrierRow={carrierRow}
                  key={carrierRow.carrierKey}
                  onSelectRate={onSelectRate}
                  selectedOfferId={selectedRate?.offerId}
                  sellerFreightProviderId={sellerFreightProviderId}
                  zIndex={zIndex}
                />
              ))}
            </div>
          )}
        </Stack>
      </div>
      <div className={styles.stickyFooter}>
        <WizardInsuranceFooter insurance={insurance} onInsuranceChange={onInsuranceChange} zIndex={zIndex} />
        <WizardCostBreakdown selectedRate={selectedRate} />
      </div>
    </div>
  );
};
